import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  Button,
  Space,
  Tag,
  Select,
  DatePicker,
  Typography,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Row,
  Col,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { projectKeys } from '@/hooks/useProjects';
import {
  projectService,
  type ProjectTimesheetRow,
} from '@/services/project.service';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_PROJECT_TIMESHEET } from '@/constants/approval-entity-types';
import { CostControlLineFields } from '@/components/project/CostControlLineFields';
import { BudgetExceedanceRequestModal } from '@/components/project/BudgetExceedanceRequestModal';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export const ProjectTimesheetsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [projectId, setProjectId] = useState<string | undefined>();
  const [approvalStatus, setApprovalStatus] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([
    null,
    null,
  ]);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [postOpen, setPostOpen] = useState(false);
  const [postRow, setPostRow] = useState<ProjectTimesheetRow | null>(null);
  const [postForm] = Form.useForm();
  const [posting, setPosting] = useState(false);
  const [exceedanceOpen, setExceedanceOpen] = useState(false);
  const [exceedanceCheck, setExceedanceCheck] = useState<Record<string, unknown> | null>(null);
  const [exceedanceLines, setExceedanceLines] = useState<Array<Record<string, unknown>>>([]);

  const params = {
    project_id: projectId,
    approval_status: approvalStatus,
    date_from: dateRange[0]?.format('YYYY-MM-DD'),
    date_to: dateRange[1]?.format('YYYY-MM-DD'),
  };

  const { data: rows = [], isLoading } = useQuery({
    queryKey: [...projectKeys.all, 'company-timesheets', params],
    queryFn: () => projectService.getCompanyTimesheets(params),
  });

  const { data: projects = [] } = useQuery({
    queryKey: projectKeys.list(),
    queryFn: () => projectService.getProjects(),
    staleTime: 60_000,
  });

  const { data: costCenters = [] } = useQuery({
    queryKey: [...projectKeys.all, 'cost-centers'],
    queryFn: () => projectService.getProjectCostCenters(),
    staleTime: 60_000,
  });

  const openPostModal = (row: ProjectTimesheetRow) => {
    setPostRow(row);
    postForm.setFieldsValue({
      cost_center_id: undefined,
      wbs_node_id: null,
      cbs_code_id: null,
      cost_category: 'Labor',
    });
    setPostOpen(true);
  };

  const submitPostToCost = async () => {
    if (!postRow) return;
    const values = await postForm.validateFields();
    setPosting(true);
    try {
      await projectService.postTimesheetToCost(postRow.id, {
        costCenterId: values.cost_center_id,
        wbsNodeId: values.wbs_node_id || null,
        cbsCodeId: values.cbs_code_id || null,
        costCategory: values.cost_category || 'Labor',
      });
      message.success('Posted to project cost');
      setPostOpen(false);
      setPostRow(null);
      await queryClient.invalidateQueries({ queryKey: [...projectKeys.all, 'company-timesheets'] });
      await queryClient.invalidateQueries({ queryKey: [...projectKeys.all, 'company-costs'] });
    } catch (e: any) {
      const code = e?.response?.data?.errors?.code;
      const msg = e?.response?.data?.message || e?.message || 'Post failed';
      if (code === 'BUDGET_EXCEEDANCE_REQUIRED' || /exceed|budget/i.test(String(msg))) {
        message.warning(msg);
        const lines = [
          {
            projectId: postRow.project_id,
            wbsNodeId: values.wbs_node_id || null,
            cbsCodeId: values.cbs_code_id || null,
            costCategory: values.cost_category || 'Labor',
            amount: Number(postRow.total_cost) || 0,
            sourceLineId: postRow.id,
          },
        ];
        try {
          const check = await projectService.checkCostControl(postRow.project_id, {
            lines,
            excludeSourceType: 'project_timesheet',
            excludeSourceId: postRow.id,
          });
          setExceedanceLines(lines);
          setExceedanceCheck(check);
          setExceedanceOpen(true);
        } catch {
          message.error(msg);
        }
      } else {
        message.error(msg);
      }
    } finally {
      setPosting(false);
    }
  };

  const columns: ColumnsType<ProjectTimesheetRow> = [
    {
      title: 'Work date',
      dataIndex: 'work_date',
      key: 'work_date',
      width: 120,
      render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '—'),
    },
    {
      title: 'Project',
      key: 'project',
      width: 200,
      render: (_, r) => (
        <Text>
          {r.project?.project_number ? `${r.project.project_number} — ` : ''}
          {r.project?.project_name ?? '—'}
        </Text>
      ),
    },
    {
      title: 'Resource',
      key: 'user',
      width: 160,
      render: (_, r) => {
        const u = r.user;
        const name = u ? [u.first_name, u.last_name].filter(Boolean).join(' ') : '';
        return <Text>{name || u?.email || 'Unknown user'}</Text>;
      },
    },
    {
      title: 'Hours',
      dataIndex: 'hours_worked',
      key: 'hours_worked',
      width: 80,
      align: 'right',
    },
    {
      title: 'Rate',
      dataIndex: 'hourly_rate',
      key: 'hourly_rate',
      width: 90,
      align: 'right',
    },
    {
      title: 'Cost',
      dataIndex: 'total_cost',
      key: 'total_cost',
      width: 100,
      align: 'right',
      render: (v: number) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }),
    },
    {
      title: 'Status',
      dataIndex: 'approval_status',
      key: 'approval_status',
      width: 110,
      render: (s: string) => {
        const color =
          s === 'Approved' ? 'green' : s === 'Rejected' ? 'red' : 'default';
        return <Tag color={color}>{s}</Tag>;
      },
    },
  ];

  const submitTimesheet = async () => {
    const values = await form.validateFields();
    const pid = values.project_id as string;
    setSaving(true);
    try {
      await projectService.createTimesheet(pid, {
        work_date: values.work_date.format('YYYY-MM-DD'),
        hours_worked: values.hours_worked,
        hourly_rate: values.hourly_rate ?? 0,
        is_billable: values.is_billable ?? true,
        billable_hours: values.billable_hours ?? values.hours_worked,
        billing_rate: values.billing_rate ?? values.hourly_rate ?? 0,
        activity_description: values.activity_description,
        task_category: values.task_category,
        department_id: values.department_id,
      });
      await queryClient.invalidateQueries({ queryKey: projectKeys.all });
      await queryClient.invalidateQueries({ queryKey: [...projectKeys.all, 'company-timesheets'] });
      setModalOpen(false);
      form.resetFields();
      message.success('Time entry saved');
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to save time entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Timesheets</Title>
        <Text type="secondary">Company-wide project time entries</Text>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Project"
          style={{ width: 260 }}
          value={projectId}
          onChange={setProjectId}
          showSearch
          optionFilterProp="children"
        >
          {projects.map((p) => (
            <Select.Option key={p.id} value={p.id}>
              {p.project_number} — {p.project_name}
            </Select.Option>
          ))}
        </Select>
        <Select
          allowClear
          placeholder="Approval"
          style={{ width: 140 }}
          value={approvalStatus}
          onChange={setApprovalStatus}
        >
          <Select.Option value="Pending">Pending</Select.Option>
          <Select.Option value="Approved">Approved</Select.Option>
          <Select.Option value="Rejected">Rejected</Select.Option>
        </Select>
        <RangePicker value={dateRange as any} onChange={(v) => setDateRange(v || [null, null])} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          Log time
        </Button>
      </Space>

      <Table<ProjectTimesheetRow>
        rowKey="id"
        loading={isLoading}
        columns={[
          ...columns,
          {
            title: 'Workflow',
            key: 'wf',
            width: 140,
            render: (_: unknown, r: ProjectTimesheetRow) =>
              r.approval_status === 'Pending' && !r.approval_instance_id ? (
                <Button
                  size="small"
                  onClick={async () => {
                    try {
                      await projectService.submitTimesheetForUniversalApproval(r.id);
                      await queryClient.invalidateQueries({ queryKey: [...projectKeys.all, 'company-timesheets'] });
                    } catch (e) {
                      console.error(e);
                    }
                  }}
                >
                  Submit approval
                </Button>
              ) : null,
          },
          {
            title: 'Cost',
            key: 'post',
            width: 130,
            render: (_: unknown, r: ProjectTimesheetRow) => {
              if (r.posted_to_project_cost || r.is_posted) {
                return <Tag color="green">Posted</Tag>;
              }
              if (r.approval_status !== 'Approved') return null;
              return (
                <Button size="small" type="link" onClick={() => openPostModal(r)}>
                  Post to cost
                </Button>
              );
            },
          },
        ]}
        dataSource={rows}
        scroll={{ x: 1000 }}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        expandable={{
          expandedRowRender: (r: ProjectTimesheetRow) =>
            r.approval_instance_id ? (
              <div style={{ padding: 12, background: '#fafafa' }}>
                <ApprovalPanel
                  entityType={ENTITY_TYPE_PROJECT_TIMESHEET}
                  entityId={r.id}
                  onApproveViaDocument={async (remarks) => {
                    await projectService.approveTimesheet(r.id, remarks);
                    await queryClient.invalidateQueries({ queryKey: [...projectKeys.all, 'company-timesheets'] });
                  }}
                  onRejectViaDocument={async (payload) => {
                    await projectService.rejectTimesheet(r.id, payload);
                    await queryClient.invalidateQueries({ queryKey: [...projectKeys.all, 'company-timesheets'] });
                  }}
                  extraInvalidateQueryKeys={[[...projectKeys.all, 'company-timesheets']]}
                />
              </div>
            ) : null,
          rowExpandable: (r: ProjectTimesheetRow) => !!r.approval_instance_id,
        }}
      />

      <Modal
        title="Log time"
        open={modalOpen}
        onOk={submitTimesheet}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        confirmLoading={saving}
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="project_id"
            label="Project"
            rules={[{ required: true, message: 'Select project' }]}
          >
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Project"
              onChange={(pid: string) => {
                const p = projects.find((x) => x.id === pid);
                if (p?.department_id) {
                  form.setFieldValue('department_id', p.department_id);
                }
              }}
            >
              {projects.map((p) => (
                <Select.Option key={p.id} value={p.id}>
                  {p.project_number} — {p.project_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="work_date"
                label="Work date"
                rules={[{ required: true, message: 'Required' }]}
                initialValue={dayjs()}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="hours_worked"
                label="Hours worked"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={0.25} step={0.25} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="hourly_rate" label="Hourly rate" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_billable"
                label="Billable"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="billable_hours" label="Billable hours">
                <InputNumber min={0} step={0.25} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="billing_rate" label="Billing rate">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="task_category" label="Task category">
            <Input placeholder="e.g. Installation" />
          </Form.Item>
          <Form.Item name="activity_description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Post timesheet to project cost"
        open={postOpen}
        onOk={submitPostToCost}
        onCancel={() => {
          setPostOpen(false);
          setPostRow(null);
        }}
        confirmLoading={posting}
        okText="Post"
        width={520}
      >
        {postRow && (
          <Form form={postForm} layout="vertical">
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              {postRow.project?.project_number} · {Number(postRow.hours_worked)} hrs ·{' '}
              {Number(postRow.total_cost).toLocaleString()}
            </Text>
            <Form.Item
              name="cost_center_id"
              label="Cost center"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Select
                showSearch
                optionFilterProp="children"
                placeholder={costCenters.length ? 'Cost center' : 'No cost centers'}
              >
                {costCenters.map((cc) => (
                  <Select.Option key={cc.id} value={cc.id}>
                    {cc.cost_center_code} — {cc.cost_center_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Cost control (WBS / CBS)">
              <Form.Item noStyle shouldUpdate>
                {() => (
                  <CostControlLineFields
                    projectId={postRow.project_id}
                    value={{
                      wbs_node_id: postForm.getFieldValue('wbs_node_id'),
                      cbs_code_id: postForm.getFieldValue('cbs_code_id'),
                      cost_category: postForm.getFieldValue('cost_category') || 'Labor',
                    }}
                    onChange={(patch) =>
                      postForm.setFieldsValue({
                        wbs_node_id: patch.wbs_node_id,
                        cbs_code_id: patch.cbs_code_id,
                        cost_category: patch.cost_category || 'Labor',
                      })
                    }
                    size="middle"
                  />
                )}
              </Form.Item>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {postRow && (
        <BudgetExceedanceRequestModal
          open={exceedanceOpen}
          onClose={() => setExceedanceOpen(false)}
          projectId={postRow.project_id}
          sourceEntityType="project_timesheet"
          sourceEntityId={postRow.id}
          lines={exceedanceLines}
          checkResult={exceedanceCheck as any}
        />
      )}
    </div>
  );
};
